import React, {useCallback, useMemo} from 'react';
import {Button, ButtonGroup, Checkbox, Grid, Stack, TextField} from "@mui/material";
import {inputChangeEvent, mapSetState, SetState} from "../../util/FormHandlers";
import {isoDate, month, plusWeeks, WeekDay, WeekDayShortNames, weekNumber, withDayOfWeek} from "../../util/DateUtil";
import Divider from "@mui/material/Divider";
import {useCurrentOfficeStore} from "../../api/CurrentOfficeStore";
import {BERLIN_OFFICE_ID} from "../../util/OfficePath";
import {EmployeeVisitType} from "../../api/EmployeeVisitStore";
import {OfficeVisitSheet} from "../../api/Model";

type ExportVisitSheet = OfficeVisitSheet<{fullName: string}, EmployeeVisitType>;

export interface ExportForDatesForm {
    reportName: string;
    selectedDates: Set<string>;
    sheet: ExportVisitSheet;
}

const weekWorkDays = [
    WeekDay.Monday,
    WeekDay.Tuesday,
    WeekDay.Wednesday,
    WeekDay.Thursday,
    WeekDay.Friday,
];

function ReportNameAdornment({sheet, setReportName, toggleAll}: {
    sheet: ExportVisitSheet,
    setReportName: SetState<string>,
    toggleAll: (dayOfWeek: number, checked: boolean) => void
}) {
    const currentOfficeId = useCurrentOfficeStore(state => state.officeId);

    const handlers = useMemo(() => {
        function setReport(name: string, daysOfWeek: number[]) {
            const months = [...new Set(sheet.dates.map(date => month(new Date(date))))].sort();
            setReportName(months.length === 0 ? (
                `${name}.csv`
            ) : months.length === 1 ? (
                `${months[0]}-${name}.csv`
            ) : (
                `${months[0]}-${months[months.length - 1]}-${name}.csv`
            ));
            weekWorkDays.forEach(dayOfWeek => toggleAll(dayOfWeek, daysOfWeek.includes(dayOfWeek)));
        }

        return {
            handleBreakfast: () => setReport('Berlin-Breakfast', [WeekDay.Tuesday, WeekDay.Thursday]),
            handleLunch: () => setReport('Berlin-Lunch', [WeekDay.Friday]),
        };
    }, [sheet, setReportName, toggleAll]);

    return currentOfficeId === BERLIN_OFFICE_ID ? (
        <ButtonGroup>
            <Button variant="text" onClick={handlers.handleBreakfast}>Breakfast</Button>
            <Button variant="text" onClick={handlers.handleLunch}>Lunch</Button>
        </ButtonGroup>
    ) : null;
}

export function ExportForDatesDialog({form, setForm}: {
    form: ExportForDatesForm,
    setForm: SetState<ExportForDatesForm>,
}) {
    const weeks = useMemo(
        () => getWorkingDaysInRange(form.sheet),
        [form.sheet],
    );

    const setSelectedDates = useMemo(() => {
        return mapSetState(setForm, 'selectedDates');
    }, [setForm])

    const setReportName = useMemo(() => {
        return mapSetState(setForm, 'reportName');
    }, [setForm]);

    const handleToggle = useCallback((date: string) => {
        setSelectedDates(prev => {
            const next = new Set(prev);
            if (next.has(date)) {
                next.delete(date);
            } else {
                next.add(date);
            }
            return next;
        });
    }, [setSelectedDates]);

    const allEnabled = useMemo(() => {
        const allEnabled: Record<number, 'all' | 'none' | 'some'> = {};
        weekWorkDays.forEach(weekDay => {
            const all = weeks.every(week => {
                const workDay = week.workDays[weekDay];
                return !workDay.enabled || form.selectedDates.has(workDay.date);
            });
            const none = weeks.every(week => {
                const workDay = week.workDays[weekDay];
                return !workDay.enabled || !form.selectedDates.has(workDay.date);
            });
            allEnabled[weekDay] = all ? 'all' : none ? 'none' : 'some';
        });
        return allEnabled;
    }, [form.selectedDates, weeks]);

    const handleToggleAll = useCallback((dayOfWeek: number, checked: boolean) => {
        setSelectedDates(prev => {
            const next = new Set(prev);
            weeks.forEach(week => {
                const workDay = week.workDays[dayOfWeek];
                if (workDay.enabled) {
                    if (checked) {
                        next.add(workDay.date);
                    } else {
                        next.delete(workDay.date);
                    }
                }
            })
            return next;
        });
    }, [setSelectedDates, weeks]);

    return (
        <Stack direction="column" gap={1} minWidth={800}>
            <TextField
                label="Report name"
                required
                value={form.reportName}
                onChange={inputChangeEvent(setReportName)}
                fullWidth
                margin="dense"
                slotProps={{
                    input: {
                        endAdornment: (
                            <ReportNameAdornment
                                sheet={form.sheet}
                                setReportName={setReportName}
                                toggleAll={handleToggleAll}
                            />
                        )
                    }
                }}
            />
            <Grid container gap={1} alignItems="center">
                <Grid size={2} sx={{fontWeight: 'bold'}}>Week</Grid>
                {weekWorkDays.map(day => {
                    return (
                        <Grid size={1.8} key={day} sx={{textAlign: 'center', fontWeight: 'bold'}}>
                            {WeekDayShortNames[day]}
                        </Grid>
                    );
                })}
            </Grid>
            <Grid container gap={1} alignItems="center">
                <Grid size={2} sx={{fontWeight: 'bold'}}>&nbsp;</Grid>
                {weekWorkDays.map(day => {
                    return (
                        <Grid size={1.8} key={day} sx={{textAlign: 'center'}}>
                            <Checkbox
                                checked={allEnabled[day] === 'all'}
                                indeterminate={allEnabled[day] === 'some'}
                                onChange={event => handleToggleAll(day, event.target.checked)}
                            />
                        </Grid>
                    );
                })}
            </Grid>
            <Divider/>
            {weeks.map(week => (
                <Grid container key={week.weekName} gap={1} alignItems="center">
                    <Grid size={2}>{week.weekName}</Grid>
                    {weekWorkDays.map(day => {
                        const {enabled, date} = week.workDays[day];
                        return (
                            <Grid size={1.8} key={day} sx={{textAlign: 'center'}}>
                                <Checkbox
                                    checked={form.selectedDates.has(date)}
                                    onChange={() => handleToggle(date)}
                                    disabled={!enabled}
                                    title={date}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            ))}
        </Stack>
    );
}

type WorkingWeek = {
    weekName: string,
    workDays: Record<number, { enabled: boolean, date: string }>,
};

function getWorkingDaysInRange(sheet: ExportVisitSheet): WorkingWeek[] {
    const fromDate = new Date(sheet.dates[0]);
    const toDate = new Date(sheet.dates[sheet.dates.length - 1]);

    let weekMonday = withDayOfWeek(
        fromDate.getDay() <= WeekDay.Friday ? fromDate : plusWeeks(fromDate, 1),
        WeekDay.Monday
    );

    const weeks: WorkingWeek[] = [];
    do {
        const currentWeekMonday = weekMonday;
        weeks.push({
            weekName: `W${currentWeekMonday.getFullYear()}-${weekNumber(currentWeekMonday)}`,
            workDays: Object.fromEntries(
                weekWorkDays.map(dayOfWeek => {
                    const date = withDayOfWeek(currentWeekMonday, dayOfWeek);
                    return [
                        dayOfWeek,
                        {
                            enabled: fromDate <= date && date <= toDate,
                            date: isoDate(date)
                        }
                    ];
                })
            ) as WorkingWeek['workDays'],
        });
        weekMonday = plusWeeks(weekMonday, 1);
    } while (weekMonday <= toDate);
    return weeks;
}
